// When the "new version available" prompt is allowed to interrupt.
//
// Kept apart from the hook so it can be tested without dragging WorkoutContext
// (and the ESM-only LLM clients behind it) into Jest.

// Coach states during which an update prompt is unwelcome.
//
// - "active": a prompt under a bar is hostile. restoreState() would recover the
//   position on reload, but that is not the point — the interruption is.
// - "planning": a reload throws away an LLM round-trip the user is waiting on.
//
// The waiting worker keeps indefinitely, so holding the prompt costs nothing.
const BUSY_APP_STATES = new Set(["active", "planning"]);

/**
 * @param {object} args
 * @param {boolean} args.updateReady  A new build is installed and waiting.
 * @param {string}  args.appState     "ready" | "planning" | "active" | "finished".
 * @param {boolean} args.prompted     This page has already offered the update.
 * @returns {boolean}
 */
export function canPromptUpdate({ updateReady, appState, prompted }) {
  if (!updateReady || prompted) return false;
  return !BUSY_APP_STATES.has(appState);
}

/**
 * Whether to take the waiting build without asking: only when the app is idle
 * (no session, generation or finished-session screen) AND the user hasn't
 * touched it since it was opened or brought back. Then nothing they are doing
 * can be lost, and a reload is just the app opening on the latest version.
 *
 * @param {object} args
 * @param {boolean} args.updateReady  A new build is installed and waiting.
 * @param {string}  args.appState     "ready" | "planning" | "active" | "finished".
 * @param {boolean} args.interacted   Any tap or key press since launch/resume.
 * @returns {boolean}
 */
export function canAutoApplyUpdate({ updateReady, appState, interacted }) {
  return Boolean(updateReady) && appState === "ready" && !interacted;
}

export default canPromptUpdate;
